import { effect, inject } from '@angular/core';
import { APP_SESSION } from '@indutrust/shared-model';
import { signalStoreFeature, withHooks } from '@ngrx/signals';
import type { EntityId } from '@ngrx/signals/entities';
import { injectDispatch } from '@ngrx/signals/events';
import type { createCrudEvents } from '../crud-entities/crud-event/crud-entities.events';

/**
 * A signal store feature that automatically calls `store.getAll()` when the
 * user becomes authenticated.
 *
 * Uses the {@link APP_SESSION} abstraction so that `scope:shared` libraries
 * remain decoupled from any concrete auth implementation.
 *
 * @returns A signal store feature that triggers data loading on authentication.
 */
export function withLoadAllOnAuth() {
  return signalStoreFeature(
    withHooks((store) => {
      const session = inject(APP_SESSION);

      return {
        onInit() {
          effect(() => {
            if (session.isAuthenticated()) {
              if ('getAll' in store && typeof store.getAll === 'function') {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                store.getAll();
              }
            }
          });
        },
      };
    })
  );
}

/**
 * A signal store feature that dispatches a `loadAllRequested` CRUD event when
 * the user becomes authenticated.
 *
 * Uses the {@link APP_SESSION} abstraction so that `scope:shared` libraries
 * remain decoupled from any concrete auth implementation (the brand's `config`
 * library provides `APP_SESSION` wired to its auth strategy).
 *
 * @template Entity - The entity type with an `id` property.
 * @param crudEvents - The CRUD events group used to dispatch the `loadAllRequested` event.
 * @returns A signal store feature that dispatches `loadAllRequested` on authentication.
 */
export function withLoadAllOnAuthEvents<Entity extends { id: EntityId }>(
  crudEvents: ReturnType<typeof createCrudEvents<Entity>>
) {
  return signalStoreFeature(
    withHooks(() => {
      const session = inject(APP_SESSION);
      const dispatch = injectDispatch(crudEvents);

      return {
        onInit() {
          effect(() => {
            if (session.isAuthenticated()) {
              dispatch.loadAllRequested({});
            }
          });
        },
      };
    })
  );
}
